/**
 * Single entry point for Mixpanel.
 *
 * mixpanel-browser touches browser globals as soon as it is evaluated, so it is
 * imported lazily and only in prod (the only env where it is initialised).
 * Every operation is chained onto one queue so `identify` → `track` ordering
 * holds even when callers don't await; auth code still awaits explicitly.
 * Analytics must never break the caller, so every operation swallows errors.
 */
import type { Mixpanel } from 'mixpanel-browser'

export type LoginMethod = 'email' | 'passkey' | 'google'
export type OrderSource = 'order-form' | 'chat' | 'one-tap' | 'quick-order' | 'chart'

type Exchange = string

/** Typed contract: event name → property shape. */
export interface AnalyticsEvents {
  'Sign up': { method: LoginMethod }
  'Log in': { method: LoginMethod }
  'Add 2FA': Record<string, never>
  'Connect Exchange Account': {
    exchange: Exchange
    account_value_usd?: number
    reconnect?: boolean
  }
  'Opened Instrument': { exchange: Exchange; ticker: string }
  'Added Indicator': { indicator_name: string; exchange?: Exchange; ticker?: string }
  'Create Watchlist': Record<string, never>
  'Submit Order': {
    order_type: string
    exchange?: Exchange
    instrument: string
    size: number
    size_denomination?: string
    source: OrderSource
  }
  'Start Delta Hedge': { exchange?: Exchange; instrument: string }
  'Start Arbitrage': {
    leg1_exchange?: Exchange
    leg1_instrument: string
    leg2_exchange?: Exchange
    leg2_instrument: string
  }
  'Create Page': { template_name: string }
  'Renamed Page': { page_name: string }
  'Add Widget to Page': { widget_name: string }
  'Configured Positions Table Columns': { visible_columns: string[] }
  // Existing runtime telemetry — payloads are owned by their reporters.
  runtime_recovery: Record<string, unknown>
  runtime_recovery_level3: Record<string, unknown>
  ws_stream_performance: Record<string, unknown>
}

export type AnalyticsEventName = keyof AnalyticsEvents

// process.env is read at call time (Next inlines NEXT_PUBLIC_* at build), matching
// the pre-existing prod gate in instrumentation-client.ts.
const isEnabled = () => process.env.NEXT_PUBLIC_ENV === 'prod' && typeof window !== 'undefined'

let clientPromise: Promise<Mixpanel> | null = null
let queue: Promise<unknown> = Promise.resolve()

/** Shared init: the SDK is loaded and initialised exactly once. */
function getClient(): Promise<Mixpanel> {
  if (!clientPromise) {
    clientPromise = import('mixpanel-browser').then(({ default: mixpanel }) => {
      mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN ?? '', {
        api_host: 'https://track.orderx.com',
        // Analytics events are intentionally manual. Keep Mixpanel from emitting
        // [Auto] Page View or DOM interaction events on its own.
        track_pageview: false,
        autocapture: false,
        persistence: 'localStorage',
      })
      return mixpanel
    })
  }
  return clientPromise
}

function enqueue(op: (mixpanel: Mixpanel) => void): Promise<void> {
  if (!isEnabled()) return Promise.resolve()
  const next = queue.then(async () => {
    try {
      op(await getClient())
    } catch {
      // Telemetry must never break the caller.
    }
  })
  queue = next
  return next
}

/** Starts the SDK without emitting an event. */
export function initAnalytics(): Promise<void> {
  return enqueue(() => {})
}

export function identify(userId: string): Promise<void> {
  return enqueue((mixpanel) => mixpanel.identify(userId))
}

export function track<E extends AnalyticsEventName>(
  event: E,
  props?: AnalyticsEvents[E]
): Promise<void> {
  return enqueue((mixpanel) => mixpanel.track(event, props))
}

/** Not idempotent: every call mints a new anonymous distinct id. Call once per logout. */
export function reset(): Promise<void> {
  return enqueue((mixpanel) => mixpanel.reset())
}
